import type { ReactNode } from 'react';

export function fmtUsd(n: number, digits = 2): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(n);
}

export function fmtPct(n: number): string {
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}%`;
}

export function fmtNum(n: number, digits = 2): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: digits });
}

export function shortId(id: string): string {
  if (id.length <= 14) return id;
  return `${id.slice(0, 8)}…${id.slice(-4)}`;
}

export function cls(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

export function Gauge({ value, label }: { value: number; label: string }) {
  const pct = Math.round(Math.min(1, Math.max(0, value)) * 100);
  const hue = pct >= 70 ? 152 : pct >= 45 ? 45 : 350;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px] uppercase tracking-wider text-slate-500">
        <span>{label}</span>
        <span className="font-mono text-slate-300">{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{
            width: `${pct}%`,
            background: `linear-gradient(90deg, hsl(${hue} 70% 45%), hsl(${hue} 80% 58%))`,
          }}
        />
      </div>
    </div>
  );
}

/** Mini SVG sparkline for index history */
export function Sparkline({
  data,
  width = 120,
  height = 36,
  stroke = '#22d3ee',
  className,
}: {
  data: number[];
  width?: number;
  height?: number;
  stroke?: string;
  className?: string;
}) {
  if (data.length < 2) {
    return (
      <svg width={width} height={height} className={cls('opacity-30', className)}>
        <line x1="0" y1={height / 2} x2={width} y2={height / 2} stroke={stroke} strokeWidth="1" />
      </svg>
    );
  }
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - ((v - min) / range) * (height - 4) - 2;
      return `${x},${y}`;
    })
    .join(' ');
  const area = `${pts} ${width},${height} 0,${height}`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className={className}>
      <defs>
        <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.35" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={area} fill="url(#sparkFill)" />
      <polyline points={pts} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

export function Panel({
  title,
  subtitle,
  children,
  className,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cls(
        'rounded-xl border border-pulse-border bg-pulse-panel backdrop-blur-xl shadow-glow',
        className,
      )}
    >
      <header className="flex flex-col items-start gap-1 border-b border-white/5 px-3 py-3 sm:flex-row sm:items-baseline sm:justify-between sm:gap-2 sm:px-4">
        <h2 className="font-display text-sm font-semibold tracking-wide text-white">{title}</h2>
        {subtitle && (
          <span className="text-[10px] font-mono uppercase text-slate-500 sm:text-right">{subtitle}</span>
        )}
      </header>
      <div className="p-3 sm:p-4">{children}</div>
    </section>
  );
}
